import React, { useMemo, useState } from "react";

import { Link, useParams } from "react-router-dom";

import { formatTimestampShort } from "utils/format";
import {
  faBooks,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";

import Button from "components/Button";
import IndexCheck from "components/IndexCheck";
import Loading from "components/Loading";
import LogLines from "components/LogLines";
import PageContent from "components/PageContent";
import PageIssueList from "components/PageIssueList";
import Panel from "components/Panel";
import QueryTagOverview from "components/QueryTagOverview";
import QuerySamples from "components/QuerySamples";

import PageSecondaryNavigation, {
  PageNavLink,
} from "components/PageSecondaryNavigation";
import UpgradeRequired from "components/UpgradeRequired";
import { formatEstimatedCount } from "utils/format";

import Graph from "./Graph";

import styles from "./style.module.scss";

import { useFeature } from "components/OrganizationFeatures";
import DateRangeBar from "components/DateRangeBar";
import { useDateRange, useSetDateRange } from "components/WithDateRange";
import moment, { Moment } from "moment-timezone";
import { useTimeout } from "utils/hooks";
import { useRoutes } from "utils/routes";
import QueryExplainsList from "components/QueryExplainList";
import QueryExplain from "components/QueryExplain";
import PanelSection from "components/PanelSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import ExpandableSQL from "components/ExpandableSQL";
import {
  IndexAdvisorResult,
  QueryAnalysisResult,
  transformQueryAnalysis,
} from "../IndexAdvisor/util";
import QueryDetailsIndexAdvisor from "./QueryDetailsIndexAdvisor";

import ContactSupportLink from "components/ContactSupportLink";
import { ExchangeIcon, ExpandToggle, InfoIcon } from "components/Icons";
import { isMissingQueryText } from "./util";
import QueryDetailsHeader from "./QueryDetailsHeader";
import Popover from "components/Popover";
import {
  QueryDetailsExplainListQueryHookResult,
  useQueryDetailsExplainListQuery,
} from "./gql/Query.explains.generated";
import { useQueryDetailsQuery } from "./gql/Query.generated";
import {
  QueryDetailIndexAdvisorIssuesQueryHookResult,
  useQueryDetailIndexAdvisorIssuesQuery,
} from "./gql/Query.indexissues.generated";
import { QueryCounts } from "types/types";
import Grid from "components/Grid";
import Badge from "components/Badge";

export type QueryExplainType =
  QueryDetailsExplainListQueryHookResult["data"]["getQueryExplains"][number];

type IssueType =
  QueryDetailIndexAdvisorIssuesQueryHookResult["data"]["getIssues"][number];

type Props = {
  tab: string;
};

const QueryDetails: React.FunctionComponent<Props> = ({ tab }) => {
  const { databaseId, queryId, explainId } = useParams();
  const [showWorkbooks, setShowWorkbooks] = useState(false);
  const { from, to } = useDateRange();

  const hasIndexCheck = useFeature("indexCheck");
  const hasIndexAdvisor = useFeature("indexAdvisor");
  const hasWorkbooks = useFeature("workbooks");
  const {
    organizationSubscription,
    databaseWorkbooksNew,
    databaseWorkbookVariants,
  } = useRoutes();

  const {
    data,
    loading: detailsLoading,
    error: detailsError,
  } = useQueryDetailsQuery({
    variables: {
      databaseId,
      queryId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });
  const {
    data: indexAdvisorData,
    loading: indexAdvisorLoading,
    error: indexAdvisorError,
  } = useQueryDetailIndexAdvisorIssuesQuery({
    variables: {
      databaseId,
      queryId,
    },
    skip: !hasIndexAdvisor,
  });
  const loading = detailsLoading || indexAdvisorLoading;
  const error = detailsError || indexAdvisorError;
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const {
    id,
    fingerprint,
    normalizedQuery,
    normalizedQueryScanTokens,
    truncatedQuery,
    supportsWorkbooks,
    workbooks,
    queryTagSummary,
  } = data.getQueryDetails;
  const queryAnalysis = data.getQueryDetails
    .queryAnalysis as QueryAnalysisResult;
  const serverId = data.getServerDetails.humanId;
  const organizationSlug = data.getServerDetails.organization.slug;
  const trackIoTiming = data.trackIoTimingSetting?.value === "on";
  const blockSize = data.getServerDetails.blockSize;
  const { indexAdvisorTrialEndsAt } = data.getServerDetails.organization;
  const queryTruncated = normalizedQuery === "<truncated query>";
  const showQueryText = tab != "explains" && tab != "samples";

  const indexAdvisorResult = transformQueryAnalysis(queryAnalysis);

  const indexRecommendationIcon = getIndexAdvisorRecommendationIcon(
    hasIndexAdvisor,
    indexAdvisorResult,
    indexAdvisorData?.getIssues,
  );

  const showingExplainDetails = tab === "explains" && explainId;
  const layout = showingExplainDetails ? "bare" : "default";
  const showIssuesList = !showingExplainDetails;

  const newWorkbookURL = databaseWorkbooksNew(databaseId);
  // Workaround to handle the case when the time range is selected
  const newWorkbookLink = `${newWorkbookURL}${
    newWorkbookURL.includes("?") ? "&" : "?"
  }queryId=${queryId}`;

  return (
    <PageContent
      windowTitle={`Query #${id}`}
      pageControls={<DateRangeBar />}
      layout={layout}
      featureInfo={
        <QueryDetailsHeader
          serverId={serverId}
          databaseId={databaseId}
          queryId={id}
          fingerprint={fingerprint}
          truncatedQuery={truncatedQuery}
          tags={queryTagSummary}
        />
      }
      featureNav={
        <QueryDetailsNav
          databaseId={databaseId}
          queryId={queryId}
          indexRecommendationIcon={indexRecommendationIcon}
          counts={data.getQueryCounts}
        />
      }
      pageCategory="queries"
      pageName="show"
      pageTab={tab}
    >
      {showIssuesList && (
        <PageIssueList
          serverId={serverId}
          referentId={id}
          referentType="Query"
        />
      )}
      {tab == "indexadvisor" && indexAdvisorTrialEndsAt && (
        <Panel title="Index Advisor Trial Enabled">
          <PanelSection>
            <InfoIcon /> The trial for the new pganalyze Index Advisor is active
            on your account until{" "}
            {moment(indexAdvisorTrialEndsAt * 1000).format("MMMM DD, YYYY")}.{" "}
            <strong>
              <a
                href={organizationSubscription(organizationSlug)}
                target="_blank"
              >
                Upgrade to the new pricing plans
              </a>
            </strong>{" "}
            to continue using the Index Advisor.
          </PanelSection>
        </Panel>
      )}
      {normalizedQuery == "<pganalyze-collector>" && !explainId ? (
        <CollectorQueryInfoPanel />
      ) : isMissingQueryText(normalizedQuery) ? (
        <MissingQueryTextWarningPanel serverId={serverId} />
      ) : (
        showQueryText && (
          <Panel title="SQL Statement">
            <PanelSection>
              {queryTruncated ? (
                <QueryTruncated serverId={serverId} />
              ) : (
                <ExpandableSQL
                  sql={normalizedQuery}
                  databaseId={databaseId}
                  scanTokens={normalizedQueryScanTokens}
                />
              )}
            </PanelSection>
            {hasWorkbooks && !queryTruncated && (
              <>
                <PanelSection className="flex">
                  <div className="flex-auto">
                    <Popover
                      content={
                        !data.getDatabaseDetails.permittedToCreateWorkbooks ? (
                          <div>
                            You are not allowed to create new workbooks - you do
                            not have the <strong>Create Workbooks</strong>{" "}
                            permission on this database.
                          </div>
                        ) : (
                          !supportsWorkbooks && (
                            <div>Workbooks only support SELECT queries.</div>
                          )
                        )
                      }
                    >
                      <Link
                        to={newWorkbookLink}
                        className={
                          data.getDatabaseDetails.permittedToCreateWorkbooks &&
                          supportsWorkbooks
                            ? "!text-link hover:!text-link-hover focus:!text-link-focus"
                            : "pointer-events-none text-[#999999]"
                        }
                      >
                        <FontAwesomeIcon icon={faBooks} /> Create Workbook
                      </Link>
                    </Popover>
                  </div>
                  {workbooks.length > 0 &&
                    (workbooks.length == 1 ? (
                      <Link
                        to={databaseWorkbookVariants(
                          databaseId,
                          workbooks[0].id,
                        )}
                      >
                        See existing workbook
                      </Link>
                    ) : (
                      <Button
                        bare
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => setShowWorkbooks((curr) => !curr)}
                      >
                        <ExpandToggle expanded={showWorkbooks} arrow />
                        <span>
                          {showWorkbooks ? "Hide" : "See"} existing workbooks
                        </span>
                      </Button>
                    ))}
                </PanelSection>
                {showWorkbooks && (
                  <Grid
                    data={workbooks}
                    defaultSortBy="lastActivityAt"
                    pageSize={5}
                    columns={[
                      {
                        field: "name",
                        header: "Workbook",
                        width: "1fr",
                        renderer: function NameCell({ rowData, fieldData }) {
                          return (
                            <div>
                              <Link
                                to={databaseWorkbookVariants(
                                  databaseId,
                                  rowData.id,
                                )}
                              >
                                {fieldData}
                              </Link>
                              {rowData.draft && (
                                <Badge className="ml-2">Draft</Badge>
                              )}
                            </div>
                          );
                        },
                      },
                      {
                        field: "user",
                        header: "Created By",
                        width: "170px",
                        renderer: function UserCell({ fieldData }) {
                          return fieldData?.fullname;
                        },
                      },
                      {
                        field: "variantsCount",
                        header: "Variants",
                        width: "80px",
                        style: "number",
                      },
                      {
                        field: "lastActivityAt",
                        header: "Last Activity",
                        width: "250px",
                        defaultSortOrder: "DESC",
                        className: "text-right",
                        headerClassName: "text-right",
                        renderer: function LastActivityCell({ fieldData }) {
                          return formatTimestampShort(moment.unix(fieldData));
                        },
                      },
                    ]}
                  />
                )}
              </>
            )}
          </Panel>
        )
      )}
      {tab == "overview" && (
        <Graph
          databaseId={databaseId}
          queryId={id}
          trackIoTiming={trackIoTiming}
        />
      )}
      {tab == "indexcheck" &&
        (hasIndexCheck ? (
          <IndexCheck databaseId={databaseId} queryId={id} />
        ) : (
          <UpgradeRequired feature="Index Check" panelOnly />
        ))}
      {tab == "indexadvisor" && (
        <IndexAdvisorTab
          databaseId={databaseId}
          queryId={queryId}
          error={indexAdvisorResult.error}
          hasIndexAdvisor={hasIndexAdvisor}
          indexAdvisorIssues={indexAdvisorData?.getIssues}
        />
      )}
      {tab == "samples" && (
        <WithLogsFeature
          ifUnsupported={<>Query Sample collection requires Log Insights.</>}
        >
          <QuerySamples
            serverId={serverId}
            databaseId={databaseId}
            queryId={id}
          />
        </WithLogsFeature>
      )}
      {tab == "explains" && (
        <WithLogsFeature
          ifUnsupported={<>EXPLAIN plan collection requires Log Insights.</>}
        >
          <QueryExplainWrapper
            blockSize={blockSize}
            databaseId={databaseId}
            explainId={explainId}
            queryId={queryId}
          />
        </WithLogsFeature>
      )}
      {tab == "tags" && (
        <WithLogsFeature ifUnsupported={<>Query Tags require Log Insights.</>}>
          <QueryTagOverview
            organizationSlug={organizationSlug}
            databaseId={databaseId}
            tags={queryTagSummary}
          />
        </WithLogsFeature>
      )}
      {tab == "logs" && (
        <WithLogsFeature>
          <QueryLogsPanel
            {...{
              counts: data.getQueryCounts,
              databaseId,
              serverId,
              fingerprint,
              startTs: from,
              endTs: to,
            }}
          />
        </WithLogsFeature>
      )}
    </PageContent>
  );
};

const QueryExplainWrapper = ({
  databaseId,
  queryId,
  explainId,
  blockSize,
}: {
  databaseId: string;
  queryId: string;
  explainId: string;
  blockSize: number;
}) => {
  const { from, to } = useDateRange();
  const { data, loading, error } = useQueryDetailsExplainListQuery({
    variables: {
      databaseId,
      endTs: to.unix(),
      startTs: from.unix(),
      queryId,
    },
  });
  const comparablePlans = useMemo(() => {
    return data?.getQueryExplains.map((e) => ({
      id: e.humanId,
      seenAt: e.seenAt,
      fingerprint: e.fingerprint,
      label: formatTimestampShort(moment.unix(e.seenAt)),
      runtime: e.querySample?.runtimeMs,
      ioMs: e.totalBlkReadTime,
      ioBytes: e.totalSharedBlksRead * blockSize,
      totCost: e.totalCost,
    }));
  }, [data, blockSize]);

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return explainId ? (
    <QueryExplain
      databaseId={databaseId}
      queryId={queryId}
      explainId={explainId}
      blockSize={blockSize}
      comparablePlans={comparablePlans}
    />
  ) : (
    <QueryExplainsList
      queryId={queryId}
      databaseId={databaseId}
      blockSize={blockSize}
      explains={data.getQueryExplains}
    />
  );
};

const WithLogsFeature = ({
  ifUnsupported,
  children,
}: {
  ifUnsupported?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const hasLogs = useFeature("logs");
  if (!hasLogs) {
    return (
      <UpgradeRequired
        feature="Log Insights"
        additionalText={
          ifUnsupported ? (
            <>
              <span>
                <strong>Error:</strong> {ifUnsupported}
              </span>
            </>
          ) : undefined
        }
        panelOnly
      />
    );
  }
  return children;
};

const IndexAdvisorTab: React.FunctionComponent<{
  databaseId: string;
  queryId: string;
  error?: string;
  hasIndexAdvisor: boolean;
  indexAdvisorIssues: IssueType[];
}> = ({ databaseId, queryId, error, hasIndexAdvisor, indexAdvisorIssues }) => {
  if (!hasIndexAdvisor) {
    return <UpgradeRequired feature="Index Advisor" panelOnly />;
  }
  if (error) {
    return (
      <Panel
        title={
          <span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className={styles.indexAdvisorError}
            />{" "}
            Index Advisor Error
          </span>
        }
      >
        <PanelSection>
          <p>
            <strong>Error:</strong> Index Advisor could not analyze this query
          </p>
          <p>
            <strong>Details:</strong> {error}
          </p>
          <p>
            Please{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://pganalyze.com/docs/index-advisor"
            >
              review the documentation
            </a>{" "}
            (including current limitations) or{" "}
            <ContactSupportLink>contact support</ContactSupportLink>.
          </p>
        </PanelSection>
      </Panel>
    );
  }
  return (
    <QueryDetailsIndexAdvisor
      databaseId={databaseId}
      queryId={queryId}
      issues={indexAdvisorIssues}
    />
  );
};

const QueryLogsPanel: React.FunctionComponent<{
  databaseId: string;
  serverId: string;
  fingerprint: string;
  counts: QueryCounts;
  startTs: Moment;
  endTs: Moment;
}> = ({ databaseId, serverId, fingerprint, startTs, endTs, counts }) => {
  const [jumped, setJumped] = useState(false);
  const [, setRecheckTime] = useState(false);
  const setRange = useSetDateRange();
  // If we're current when we mount, check again after we expect to be stale
  const thresholdMinutes = 5;
  useTimeout(
    () => setRecheckTime(true),
    moment.duration(thresholdMinutes * 1.1, "minutes").asMilliseconds(),
    [],
  );

  const isNowish = (start: Moment, end: Moment): boolean => {
    const result =
      start.isBefore(
        moment().subtract(24, "hours").add(thresholdMinutes, "minutes"),
      ) && moment().diff(end, "minutes", true) < thresholdMinutes;
    return result;
  };

  const showJumpPrompt =
    !jumped && !isNowish(startTs, endTs) && counts.logs > 0;

  const handleJump = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.preventDefault();
    setRange({ from: moment().subtract(24, "hours"), to: moment() });
    setJumped(true);
  };

  return (
    <Panel title={`Log Entries (up to ${formatTimestampShort(endTs)})`}>
      {showJumpPrompt ? (
        <div className={styles.activityInfo}>
          <ExchangeIcon />
          Up to {counts.logs} log lines outside of the selected time range.{" "}
          <Button
            bare
            variant="link"
            onClick={handleJump}
            className="font-bold"
          >
            Show latest 24h
          </Button>
        </div>
      ) : null}
      <LogLines
        databaseId={databaseId}
        serverId={serverId}
        queryFingerprint={fingerprint}
        occurredBefore={endTs.unix()}
        occurredAfter={startTs.unix()}
      />
    </Panel>
  );
};

type NavProps = {
  databaseId: string;
  queryId: string;
  counts: QueryCounts;
  indexRecommendationIcon: null | number | string | React.ReactElement;
};

const QueryDetailsNav: React.FunctionComponent<NavProps> = ({
  databaseId,
  queryId,
  indexRecommendationIcon,
  counts,
}) => {
  const hasIndexAdvisor = useFeature("indexAdvisor");
  const {
    databaseQuery,
    databaseIndexCheck,
    databaseQueryIndexAdvisor,
    databaseQuerySamples,
    databaseQueryExplains,
    databaseQueryTags,
    databaseQueryLogs,
  } = useRoutes();
  return (
    <PageSecondaryNavigation>
      <PageNavLink to={databaseQuery(databaseId, queryId)}>
        Overview
      </PageNavLink>
      {hasIndexAdvisor ? (
        <PageNavLink to={databaseQueryIndexAdvisor(databaseId, queryId)}>
          Index Advisor <small>{indexRecommendationIcon}</small>
        </PageNavLink>
      ) : (
        <PageNavLink to={databaseIndexCheck(databaseId, queryId)}>
          Index Check
        </PageNavLink>
      )}
      <PageNavLink to={databaseQuerySamples(databaseId, queryId)}>
        Query Samples <small>{formatEstimatedCount(counts.samples, 5)}</small>
      </PageNavLink>
      <PageNavLink end={false} to={databaseQueryExplains(databaseId, queryId)}>
        EXPLAIN Plans <small>{formatEstimatedCount(counts.explains, 5)}</small>
      </PageNavLink>
      <PageNavLink to={databaseQueryTags(databaseId, queryId)}>
        Query Tags <small>{formatEstimatedCount(counts.tagsets, 5)}</small>
      </PageNavLink>
      <PageNavLink to={databaseQueryLogs(databaseId, queryId)}>
        Log Entries <small>{formatEstimatedCount(counts.logs, 100)}</small>
      </PageNavLink>
    </PageSecondaryNavigation>
  );
};

const CollectorQueryInfoPanel: React.FunctionComponent = () => {
  return (
    <Panel
      title={
        <span>
          <FontAwesomeIcon icon={faInfoCircle} /> Query Info
        </span>
      }
    >
      <PanelSection>
        This is an aggregation of all queries that the pganalyze collector runs
        to monitor your database.
      </PanelSection>
    </Panel>
  );
};

const MissingQueryTextWarningPanel: React.FunctionComponent<{
  serverId: string;
}> = ({ serverId }) => {
  const { serverEdit } = useRoutes();
  return (
    <Panel
      title={
        <span>
          <FontAwesomeIcon icon={faExclamationTriangle} /> Warning: could not
          capture query text
        </span>
      }
    >
      <PanelSection>
        This is an aggregation of multiple queries from pg_stat_statements that
        couldn't be matched to a query known by pganalyze because of churn.
        <p>
          Review the{" "}
          <a
            target="_blank"
            href="https://pganalyze.com/docs/install/troubleshooting/reset_stat_statements"
          >
            documentation
          </a>{" "}
          for recommended actions. You can enable automatic statement resets on
          the <Link to={serverEdit(serverId)}>server settings page</Link> to
          help with this issue.
        </p>
      </PanelSection>
    </Panel>
  );
};

const QueryTruncated: React.FunctionComponent<{ serverId: string }> = ({
  serverId,
}) => {
  const { serverConfigSetting } = useRoutes();
  return (
    <>
      <p>
        <InfoIcon className="mr-1.5" />
        This query is truncated.
      </p>
      <div>
        The query has been truncated as it exceeds the current size limit set by{" "}
        <Link to={serverConfigSetting(serverId, "track_activity_query_size")}>
          track_activity_query_size
        </Link>
        . If you encounter this issue frequently, consider increasing this
        limit. Be aware that increasing the limit may result in Postgres
        consuming more memory.
      </div>
    </>
  );
};

function getIndexAdvisorRecommendationIcon(
  hasIndexAdvisor: boolean,
  indexAdvisorResult: IndexAdvisorResult,
  indexAdvisorIssues: IssueType[] | undefined,
): null | number | string | React.ReactElement {
  if (!hasIndexAdvisor || "error" in indexAdvisorResult) {
    return "?";
  }

  const recommendationCount = indexAdvisorIssues.length;

  if (recommendationCount === 0) {
    return (
      <FontAwesomeIcon className={styles.indexAdvisorAOK} icon={faCheck} />
    );
  }

  return recommendationCount;
}

export default QueryDetails;
